import { useSearchHubspotContactMutation } from '@/store/apis/crmApi';
import { useGetHubspotContactsQuery } from '@/store/apis/crmApi';
import { HubspotContact } from '@/shared/types';
import { debounce } from '@/utils/debound.utils';
import { displayError } from '@/utils/display-message-utils';
import { Select, Spin } from 'antd';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormLabel } from '../Form';

interface HubspotContactSelectProps {
  onSelectContact: (contact: HubspotContact) => void;
  label?: string;
}

const HubspotContactSelect: FC<HubspotContactSelectProps> = ({
  onSelectContact,
  label = 'Search Hubspot Contact(Email, Name, Phone)',
}) => {
  const [hubspotContacts, setHubspotContacts] = useState<any>(null);
  const [contacts, setContacts] = useState<HubspotContact[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const { data: hubSpotContactsList, isLoading: isHubspotContactLoading } =
    useGetHubspotContactsQuery(
      {
        limit: 20,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  useEffect(() => {
    if (!isHubspotContactLoading && hubSpotContactsList?.data?.results) {
      const results = hubSpotContactsList.data.results;
      setContacts(results);
      const formatted = results.map((contact: any) => {
        const props = contact.properties;
        return {
          label: `${props.email || ''} - ${props.firstname || ''} ${props.lastname || ''}`,
          value: contact.id,
        };
      });
      setOptions(formatted);
    }
  }, [hubSpotContactsList]);

  const [searchHubspotContact, { isLoading: searchContactLoading }] =
    useSearchHubspotContactMutation();
  useEffect(() => {
    if (hubSpotContactsList?.data) {
      setHubspotContacts(hubSpotContactsList?.data?.results);
    }
  }, [hubSpotContactsList]);

  const debouncedFetch = useMemo(() => {
    return debounce(async (searchText: string) => {
      if (!searchText) return;
      try {
        const res = await searchHubspotContact({ email: searchText }).unwrap();
        const results = res?.data?.results || [];
        setContacts(results);
        const formatted = results.map((contact: any) => {
          const props = contact.properties;
          return {
            label: `${props.email || ''} - ${props.firstname || ''} ${props.lastname || ''}`,
            value: contact.id, // or props.email
          };
        });

        setOptions(formatted);
      } catch (error) {
        displayError(error);
      }
    }, 500);
  }, [searchHubspotContact]);

  return (
    <>
      <FormLabel label={label} style={{ marginBottom: '6px' }} />
      <Select
        showSearch
        labelInValue
        placeholder="Search name, phone, email"
        notFoundContent={
          searchContactLoading || isHubspotContactLoading ? (
            <Spin size="small" />
          ) : null
        }
        filterOption={false}
        onSearch={debouncedFetch}
        onChange={(selected) => {
          const selectedContact = contacts.find((c) => c.id === selected.value);
          if (selectedContact) {
            onSelectContact(selectedContact);
          }
        }}
        style={{ width: '100%' }}
      >
        {options.map((opt) => (
          <Select.Option key={opt.value} value={opt.value}>
            {opt.label}
          </Select.Option>
        ))}
      </Select>
    </>
  );
};

export default HubspotContactSelect;
